import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import { post } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing page for the onboarding-nudge WhatsApp link (/c/:code). Exchanges the REUSABLE
 * onboarding-link token for a session and drops the lead straight into onboarding — no email
 * typing, because the lead record already holds it. The token is not consumed, so the link keeps
 * working on repeat clicks until onboarding completes (server returns `use_login`) or it expires
 * (`invalid`); both surface the same friendly fallback below.
 */
export default function ContinueOnboarding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [failed, setFailed] = useState(false);
  // Guard against React StrictMode's double-invoke in dev (harmless since the token is reusable,
  // but avoids a duplicate request).
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const res = await post('/lead-capture/exchange-by-token', { code });
        if (res?.status === 'ok' && res.token && res.userData) {
          Cookies.set('token', res.token, { expires: 7 });
          login(res.userData);
          navigate('/onboarding', { replace: true });
          return;
        }
      } catch (err) {
        console.error('Onboarding link exchange failed', err);
      }
      setFailed(true);
    })();
  }, [code, navigate, login]);

  if (failed) {
    return (
      <div style={{ maxWidth: 480, margin: '80px auto', padding: 24, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
        <h2>This link is no longer active</h2>
        <p>
          If you've already finished setting up your child's trial, please{' '}
          <Link to="/login">log in</Link>.
        </p>
        <p>
          Otherwise you can <Link to="/start-trial">start your trial</Link> using the email you
          entered on Facebook.
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '80px auto', padding: 24, textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
      <p>Loading your details…</p>
    </div>
  );
}
